import { useCallback, useEffect, useState } from 'react';

import { isPriceTierConflictError } from '../../util/errors';
import { isContentSubscriptionListing } from '../../util/digitalSubscription';
import { normalizePriceTiers } from '../../util/priceTiers';
import { checkoutSoldOutUiState } from './checkoutSoldOutUi';

/**
 * Sold-out 409 / stale checkout listing: refresh catalog on load, keep the
 * sold-out notice until the buyer picks another option, and never auto-switch
 * to a pricier remaining tier.
 */
export const useCheckoutPriceTierRace = ({
  listing,
  orderData,
  initiateOrderError,
  speculateTransactionError,
  onRefreshCheckoutListing,
  setPageData,
  pageData,
}) => {
  const [soldOutTierIds, setSoldOutTierIds] = useState([]);
  const [unitsRefreshNonce, setUnitsRefreshNonce] = useState(0);
  const [hasEligiblePriceTier, setHasEligiblePriceTier] = useState(null);
  const [soldOutNoticeTierId, setSoldOutNoticeTierId] = useState(null);
  const [ackedConflictKey, setAckedConflictKey] = useState(null);

  const hasPriceTiers =
    !isContentSubscriptionListing(listing) &&
    !!normalizePriceTiers(listing?.attributes?.publicData?.priceTiers);

  const handleTierAvailabilityChange = useCallback(({ hasEligibleTier }) => {
    setHasEligiblePriceTier(!!hasEligibleTier);
  }, []);

  const refreshListingAndUnits = useCallback(() => {
    setUnitsRefreshNonce(n => n + 1);
    if (typeof onRefreshCheckoutListing !== 'function' || !listing?.id) {
      return;
    }
    onRefreshCheckoutListing(listing.id).then(freshListing => {
      if (!freshListing || typeof setPageData !== 'function') {
        return;
      }
      setPageData(current => ({
        ...(current || pageData),
        listing: freshListing,
      }));
    });
  }, [listing?.id, onRefreshCheckoutListing, setPageData, pageData]);

  useEffect(() => {
    if (!listing?.id) {
      return;
    }
    refreshListingAndUnits();
    // Fresh catalog on checkout entry — sessionStorage listing can be stale.
    // Depend only on listing id so a refreshed catalog does not loop.
    // (react-hooks/exhaustive-deps is not enabled in this repo's eslint config.)
  }, [listing?.id?.uuid]);

  const conflictError = isPriceTierConflictError(initiateOrderError)
    ? initiateOrderError
    : isPriceTierConflictError(speculateTransactionError)
    ? speculateTransactionError
    : null;

  useEffect(() => {
    if (!conflictError || !orderData?.tierId) {
      return;
    }
    setSoldOutTierIds(ids => (ids.includes(orderData.tierId) ? ids : ids.concat(orderData.tierId)));
    setSoldOutNoticeTierId(orderData.tierId);
    refreshListingAndUnits();
    // Intentionally omit refreshListingAndUnits — conflict + tierId are the trigger.
    // (react-hooks/exhaustive-deps is not enabled in this repo's eslint config.)
  }, [conflictError, orderData?.tierId]);

  const soldOutUi = checkoutSoldOutUiState({
    conflictError,
    orderTierId: orderData?.tierId,
    persistedNoticeTierId: soldOutNoticeTierId,
    ackedConflictKey,
    hasEligiblePriceTier,
  });

  const acknowledgeSoldOutNotice = useCallback(() => {
    setAckedConflictKey(soldOutUi.conflictKey);
    setSoldOutNoticeTierId(null);
  }, [soldOutUi.conflictKey]);

  return {
    soldOutTierIds,
    unitsRefreshNonce,
    hasEligiblePriceTier: hasEligiblePriceTier !== false,
    handleTierAvailabilityChange,
    refreshListingAndUnits,
    soldOutNoticeTierId: soldOutUi.soldOutNoticeTierId,
    soldOutNoticeActive: soldOutUi.soldOutNoticeActive,
    hidePriceTierGoBack: soldOutUi.hidePriceTierGoBack,
    hideStaleSoldOutQuote: soldOutUi.hideStaleSoldOutQuote,
    acknowledgeSoldOutNotice,
    holdAutoPick: soldOutUi.holdAutoPick,
    hasPriceTiers,
  };
};
